// wsId() strips dashes, so a leaked id can be a 32+ char bare hex run OR a dashed GUID.
const ID_VALUE = "(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|[0-9a-fA-F]{16,})";
const parentheticalWithId = new RegExp(`\\s*\\([^()]*(?:${ID_VALUE})[^()]*\\)`, "g");
const labelledId = new RegExp(`\\s*[,;]?\\s*[A-Za-z][A-Za-z0-9]*\\s*[:=]\\s*${ID_VALUE}`, "g");
const bareId = new RegExp(ID_VALUE, "g");

export const stripInternalIdentifiers = (message: string): string =>
	message
		.replace(parentheticalWithId, "")
		.replace(labelledId, "")
		.replace(bareId, "")
		.replace(/\s*\(\s*\)/g, "")
		.replace(/\s+([.,;:])/g, "$1")
		.replace(/\s{2,}/g, " ")
		.trim();

interface ValidationErrorLike {
	readonly code?: string | null;
	readonly message?: string | null;
}

const get = (obj: unknown, path: string): unknown => {
	return path.split(".").reduce<unknown>((prev, curr) => {
		if (prev && typeof prev === "object") return (prev as Record<string, unknown>)[curr];
		return undefined;
	}, obj);
};

const extractTransportMessage = (error: unknown): string => {
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	try {
		return JSON.stringify(error);
	} catch {
		return "Unknown transport error";
	}
};

interface ExtractedError {
	readonly kind: "transport" | "payload";
	readonly code: string | null;
	readonly message: string;
}

const extractFirstError = (result: { error?: unknown; data?: unknown }, errorsPath: string): ExtractedError | null => {
	if (result.error) {
		return { code: null, kind: "transport", message: extractTransportMessage(result.error) };
	}
	const errors = get(result.data, errorsPath);
	if (Array.isArray(errors) && errors.length > 0) {
		const first = errors[0] as ValidationErrorLike | null;
		return { code: first?.code ?? null, kind: "payload", message: first?.message ?? "" };
	}
	return null;
};

export interface SurfacedMutationError {
	readonly code: string | null;
	readonly displayMessage: string;
	readonly rawMessage: string;
}

const surfaceMutationError = (
	result: { error?: unknown; data?: unknown },
	errorsPath: string,
	fallbackMessage: string
): SurfacedMutationError | null => {
	const extracted = extractFirstError(result, errorsPath);
	if (!extracted) return null;

	if (extracted.kind === "transport") {
		return { code: null, displayMessage: fallbackMessage, rawMessage: extracted.message };
	}

	const rawMessage = extracted.message.trim() ? extracted.message : fallbackMessage;
	const displayMessage = stripInternalIdentifiers(rawMessage) || fallbackMessage;
	return { code: extracted.code, displayMessage, rawMessage };
};

export default surfaceMutationError;
